import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FiShield, FiArrowLeft } from 'react-icons/fi';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { user, login, authError, requires2FA, pendingUser, verify2FAChallenge, cancel2FA, getFirstAccessiblePath } = useAuth();
  const navigate = useNavigate();
  const codeRef = useRef(null);

  useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);

  useEffect(() => {
    if (user) {
      // Sempre abrir a primeira seção após o login e não a última
      localStorage.removeItem('redirectAfterLogin');
      const targetPath = getFirstAccessiblePath ? getFirstAccessiblePath() : '/admin';
      navigate(targetPath, { replace: true });
    }
  }, [user, navigate, getFirstAccessiblePath]);

  useEffect(() => {
    if (requires2FA && codeRef.current) {
      codeRef.current.focus();
    }
  }, [requires2FA]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      if (!result.success) {
        setError(result.message || 'Erro ao fazer login.');
      }
    } catch (err) {
      setError('Erro de conexão.');
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await verify2FAChallenge(code);
      if (!result.success) {
        setError(result.message || 'Código inválido.');
      }
    } catch (err) {
      setError('Erro de conexão.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel2FA = () => {
    setCode('');
    cancel2FA();
  };

  if (requires2FA) {
    return (
      <div className="login-body">
        <div className="login-background"></div>
        <div className="login-container-wrapper">
          <div className="text-center mb-4">
            <div style={{
              width: 64, height: 64, borderRadius: '50%',
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <FiShield size={28} color="#fff" />
            </div>
            <h4 className="fw-bold" style={{ color: '#1e293b' }}>Verificação em duas etapas</h4>
            <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: 4 }}>
              {pendingUser?.name || pendingUser?.email || 'Usuário'}
            </p>
            <p style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
              Digite o código do seu aplicativo autenticador
            </p>
          </div>

          {error && (
            <div className="alert alert-danger" role="alert">{error}</div>
          )}

          <form onSubmit={handle2FASubmit}>
            <div className="form-group-modern">
              <label>Código de verificação</label>
              <input
                ref={codeRef}
                type="text"
                inputMode="numeric"
                className="form-control-modern text-center"
                style={{ fontSize: '1.5rem', letterSpacing: '8px', fontWeight: 700 }}
                placeholder="000000"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                autoComplete="one-time-code"
                required
              />
            </div>

            <button
              type="submit"
              className="btn-modern btn-modern-primary w-100 mt-3"
              style={{ padding: '12px', fontSize: '1rem' }}
              disabled={loading || code.length < 6}
            >
              {loading ? 'Verificando...' : 'Verificar'}
            </button>
          </form>

          <div className="text-center mt-3">
            <button
              type="button"
              className="btn btn-link btn-sm p-0"
              style={{ fontSize: '0.85rem', color: '#64748b' }}
              onClick={handleCancel2FA}
            >
              <FiArrowLeft size={14} className="me-1" />Voltar ao login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-body">
      <div className="login-background"></div>

      <div className="login-container-wrapper">
        <h2 className="text-center mb-4 fw-bold" style={{ fontSize: '2rem', color: '#1e293b' }}>
          Metas NovoTok
        </h2>

        {error && (
          <div className="alert alert-danger" role="alert">{error}</div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group-modern">
            <label>Email</label>
            <input
              type="email"
              className="form-control-modern"
              placeholder="Digite seu email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
          </div>

          <div className="form-group-modern">
            <label>Senha</label>
            <input
              type="password"
              className="form-control-modern"
              placeholder="Digite sua senha"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <div className="text-end mt-2">
              <Link
                to="/forgot-password"
                className="btn btn-link btn-sm p-0"
                style={{ fontSize: '0.85rem' }}
              >
                Esqueci minha senha
              </Link>
            </div>
          </div>

          <button
            type="submit"
            className="btn-modern btn-modern-primary w-100 mt-3"
            style={{ padding: '12px', fontSize: '1rem' }}
            disabled={loading}
          >
            {loading ? 'Entrando...' : 'Acessar Sistema'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: '30px', fontSize: '0.8rem', color: '#64748b', opacity: 0.7 }}>
          Versão 1.0.0
        </div>
      </div>
    </div>
  );
}

export default Login;
